import React, { useState, useEffect, useMemo } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useLanguage } from '@/contexts/LanguageContext';

const TOPIC_OPTIONS = [
  'Abstinência',
  'Adultos 60+',
  'Álcool',
  'Ansiedade',
  'Apadrinhamento',
  'Autocompaixão',
  'Autoconhecimento',
  'Autocontrole',
  'Bariátrica',
  'Burnout',
  'Comunicação',
  'Compras Compulsivas',
  'Dependência Digital',
  'Depressão',
  'Drogas / Psicoativos',
  'Emoções',
  'Estresse',
  'Família',
  'Feminilidade',
  'Gatilhos',
  'Internação',
  'Jogos',
  'Juventude',
  'LGBT+',
  'Limites',
  'Lucidez',
  'Masculinidade',
  'Maternidade',
  'Neurodivergência',
  'Novos na Recuperação',
  'Outras Dependências',
  'Paternidade',
  'Pornografia',
  'Prevenção de Recaída',
  'Psiquiatria & Medicação',
  'Raiva',
  'Recomeços',
  'Rede de Apoio',
  'Redes Sociais',
  'Relacionamentos',
  'Ressaca',
  'Rotina de Sobriedade',
  'Sexo',
  'Sobriedade Longa',
  'Solidão',
  'Terapia',
  'Transtornos Alimentares',
  'Tratamento',
  'Vergonha & Culpa',
  'Vida Sexual Saudável',
];

export default function CommunityTopicsScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const params = useLocalSearchParams<{ selectedTopics?: string }>();
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (params.selectedTopics) {
      try {
        const topics = JSON.parse(params.selectedTopics as string);
        setSelectedTopics(topics);
      } catch (e) {
        console.error('Error parsing selected topics:', e);
      }
    }
  }, [params.selectedTopics]);

  const getTopicLabel = (ptValue: string): string => {
    const key = `community.topics.labels.${ptValue}`;
    const label = t(key);
    return label === key ? ptValue : label;
  };

  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return TOPIC_OPTIONS;
    const q = searchQuery.trim().toLowerCase();
    return TOPIC_OPTIONS.filter(topic => getTopicLabel(topic).toLowerCase().includes(q));
  }, [searchQuery, t]);

  const handleBack = () => {
    router.back();
  };

  const handleSave = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    await AsyncStorage.setItem('selectedTopicsTemp', JSON.stringify(selectedTopics));
    router.back();
  };

  const handleTopicToggle = (topic: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedTopics(prev => {
      if (prev.includes(topic)) {
        return prev.filter(tp => tp !== topic);
      } else {
        return [...prev, topic];
      }
    });
  };

  const renderTopicPill = (topic: string) => {
    const isSelected = selectedTopics.includes(topic);
    return (
      <TouchableOpacity
        key={topic}
        style={[
          styles.pill,
          isSelected ? styles.pillSelected : styles.pillUnselected
        ]}
        onPress={() => handleTopicToggle(topic)}
      >
        <View style={styles.pillIcon}>
          {isSelected ? (
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path
                d="M12.6673 4.2915L6.25065 10.7082L3.33398 7.7915"
                stroke="#015480"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          ) : (
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M4 8H12" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M8 4V12" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
          )}
        </View>
        <Text style={[
          styles.pillText,
          isSelected ? styles.pillTextSelected : styles.pillTextUnselected
        ]}>
          {getTopicLabel(topic)}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M15.75 19L8.75 12L15.75 5" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
            </Svg>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('community.topics.title')}</Text>
          <TouchableOpacity onPress={handleSave} style={styles.saveButton}>
            <Text style={styles.saveButtonText}>{t('community.topics.save')}</Text>
          </TouchableOpacity>
        </View>

        {/* Search Input */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputWrapper}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.75428 2C10.8536 2 13.366 4.51231 13.366 7.61167C13.366 10.7111 10.8536 13.224 7.75428 13.224C4.65489 13.224 2.14258 10.7111 2.14258 7.61167C2.14258 4.51231 4.65489 2 7.75428 2Z"
                stroke="#3D3C38"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M11.5742 11.7227L13.8576 14.0001"
                stroke="#3D3C38"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
            <TextInput
              style={styles.searchInput}
              placeholder={t('community.topics.searchPlaceholder')}
              placeholderTextColor="#A19F97"
              value={searchQuery}
              onChangeText={setSearchQuery}
              returnKeyType="search"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M12 4L4 12M4 4l8 8" stroke="#3D3C38" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Topics Grid */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {filteredTopics.length > 0 ? (
            <View style={styles.topicsGrid}>
              {filteredTopics.map(renderTopicPill)}
            </View>
          ) : (
            <Text style={styles.emptyText}>{t('community.topics.notFound')}</Text>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    height: 56,
  },
  backButton: {
    width: 24,
    height: 24,
  },
  headerTitle: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
    flex: 1,
  },
  saveButton: {
    paddingVertical: 4,
  },
  saveButtonText: {
    color: '#015480',
    textAlign: 'right',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 14,
  },
  searchContainer: {
    paddingHorizontal: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#DAD8CF',
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    fontWeight: '400',
    color: '#151515',
    padding: 0,
    margin: 0,
  },
  clearButton: {
    padding: 2,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-start',
  },
  emptyText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#7E7D75',
    textAlign: 'center',
    marginTop: 32,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 12,
    borderRadius: 17,
    borderWidth: 1,
    gap: 4,
  },
  pillSelected: {
    backgroundColor: '#E6EBF0',
    borderColor: '#015480',
  },
  pillUnselected: {
    backgroundColor: '#FFFFFF',
    borderColor: '#DAD8CF',
  },
  pillIcon: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pillText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16.8,
  },
  pillTextSelected: {
    color: '#015480',
  },
  pillTextUnselected: {
    color: '#151515',
  },
});
